// 레이드 주간 장부 통합(C1a, docs/RAID-NET.md 4절): 솔로 판 영수증(save.v1, 클라 권위)과 서버 장부(D1 raid_clears — 넷 판 + 올린 솔로 영수증)를 합쳐
// 이번 주 몇 번 받았는지 센다. 순수 함수(raidUnifiedDone·raidNextN)는 고정 스텝(flow.raidClear)이 부르고, 네트워크(RaidLedgerSync)는 main.ts가 붙인다.
// - 서버 장부를 모르면(오프라인·게스트·레이드 넷 꺼짐·주가 다름) 로컬 영수증만 센다 = 예전 동작 그대로.
// - 아는 경우: 센 것 = {로컬 영수증 → 'solo:<id>'} ∪ {서버 줄}. 서버 줄 중 이 캐릭터가 올린 솔로 영수증은 같은 이름이라 한 번만 센다.
// - 올리기: 로컬 영수증(이번 주 + 하드 첫 클리어) 중 서버 장부에 아직 없는 것을 POST /api/raid/solo로. 서버가 INSERT 문장 안에서 3회를 센다.
//   오프라인에서 이미 받은 보상은 되돌리지 않는다(넘치면 'over' — 다음 판부터 장부가 합쳐 막는다). 서버는 솔로 보상을 다시 주지 않는다(셈만) → 중복 지급 0.
import { parseRaidReceipt, raidDef, raidFirstId, raidReceiptId, hasHard, type RaidId } from '../shared/content/raid';

/** 서버 장부의 이번 주 모습(GameSim.raidServer). ids = 'solo:<영수증 id>' | 'net:<판 id>' | 'other:<줄 번호>' (other는 다른 계정 줄이라 id를 알리지 않고 줄 번호만 — 세기용) */
export interface RaidServerLedger { readonly raid: RaidId; readonly week: number; readonly ids: readonly string[]; readonly first: boolean }

/** 이번 주 로컬 레이드 영수증 id(노말·하드, n 1..주 한도 — 빈 자리가 있어도 센다) */
export function raidWeekReceipts(has: (id: string) => boolean, raid: RaidId, week: number): string[] {
  const out: string[] = [];
  const limit = raidDef(raid).weeklyLimit;
  for (let n = 1; n <= limit; n++) {
    const a = raidReceiptId(raid, week, n), b = raidReceiptId(raid, week, n, 'hard');
    if (has(a)) out.push(a);
    if (hasHard(raid) && has(b)) out.push(b);
  }
  return out;
}

/** 이번 주 받은 보상 수(로컬 ∪ 서버). 서버 장부가 없거나 다른 주·다른 레이드면 로컬만 */
export function raidUnifiedDone(local: readonly string[], server: RaidServerLedger | null, raid: RaidId, week: number): number {
  if (!server || server.raid !== raid || server.week !== week) return local.length;
  const s = new Set(server.ids);
  for (const id of local) s.add('solo:' + id);
  return s.size;
}

/**
 * 새 영수증의 n(1..한도): 합친 셈 + 1이 비어 있으면 그것(n = 1이면 그 주 첫 클리어 — 보호 주문서), 아니면 가장 작은 빈 자리.
 * 빈 자리가 없으면 0(연습)
 */
export function raidNextN(has: (id: string) => boolean, raid: RaidId, week: number, done: number, server: RaidServerLedger | null = null): number {
  const limit = raidDef(raid).weeklyLimit;
  if (done >= limit) return 0;
  // 서버 장부에 이미 올라간 영수증 자리도 찬 자리다 — 저장이 유실된 기기(로컬 영수증 없음)가 이미 센 n을 다시 받아 같은 보상을 두 번 받지 않게(정확성 P2-2)
  const srv = new Set(server && server.raid === raid && server.week === week ? server.ids : []);
  const free = (n: number) => !has(raidReceiptId(raid, week, n)) && !has(raidReceiptId(raid, week, n, 'hard'))
    && !srv.has('solo:' + raidReceiptId(raid, week, n)) && !srv.has('solo:' + raidReceiptId(raid, week, n, 'hard'));
  if (free(done + 1)) return done + 1;
  for (let n = 1; n <= limit; n++) if (free(n)) return n;
  return 0;
}

/** 서버 첫 클리어가 이미 있나(넷 판이나 다른 기기 솔로로) */
export function raidServerFirst(server: RaidServerLedger | null, raid: RaidId): boolean {
  return !!server && server.raid === raid && server.first;
}

// ── 네트워크(브라우저) ─────────────────────────────────────

/** RaidLedgerSync가 읽고 쓰는 게임 쪽(GameSim이 맞는다) */
export interface RaidLedgerGame {
  raidServer: RaidServerLedger | null;
  raidWeek(): number;
  readonly save: { hasResult(id: string): boolean; forEachRaidReceipt(fn: (id: string) => void): void };
}
export type LedgerFetch = (input: string, init?: RequestInit) => Promise<Response>;
export interface RaidLedgerOptions {
  /** 지금 캐릭터 id(없으면 'legacy') */
  characterId?: () => string;
  fetch?: LedgerFetch;
  base?: string;
  raid?: RaidId;
  timeoutMs?: number;
  now?: () => number;
  /** 최소 간격에 걸린 강제 sync를 뒤따라 실행할 타이머(시험이 주입, 기본 setTimeout) */
  setTimer?: (fn: () => void, ms: number) => unknown;
}
interface LedgerBody { linked?: boolean; raid?: string; week?: number; rows?: { n: number; src: string; id: string }[]; first?: boolean }

/** 서버 응답 장부 → 게임 쪽 모습(연동 없음·형식 오류면 null) */
export function ledgerFromBody(b: LedgerBody | null | undefined, raid: RaidId): RaidServerLedger | null {
  if (!b || b.linked !== true || b.raid !== raid || !Number.isSafeInteger(b.week) || !Array.isArray(b.rows)) return null;
  const ids: string[] = [];
  for (const r of b.rows) {
    if (!r || typeof r.id !== 'string') continue;
    ids.push((r.src === 'solo' ? 'solo:' : r.src === 'net' ? 'net:' : 'other:') + r.id);
  }
  return { raid, week: b.week as number, ids, first: b.first === true };
}

/** 실패(오프라인·429·5xx)가 이어질 때 다시 묻기까지: 10초 → 20 → 40 → 60초(상한). 성공하면 처음으로 */
export const LEDGER_BACKOFF_MS = { first: 10_000, max: 60_000 } as const;
/** 강제 sync(결과 직후·방 입장 직전)도 이보다 자주 네트워크를 치지 않는다 — 로그인·연동 이벤트(wake)만 예외 */
export const LEDGER_MIN_FORCE_GAP_MS = 2_000;

/**
 * 솔로 영수증 올리기·서버 장부 읽기. sync()는 동시에 하나만 돈다(겹친 호출은 진행 중인 것을 기다린다).
 * 결과가 counted·over·taken·stale·bad인 영수증은 이 세션에서 다시 올리지 않는다(later는 다음 sync에 다시).
 * 실패하면 지수 백오프(LEDGER_BACKOFF_MS) 동안 일반 sync는 쉰다. 강제 sync(force)는 조용한 시간·백오프를 무시하되 최소 간격(LEDGER_MIN_FORCE_GAP_MS)은 지킨다.
 */
export class RaidLedgerSync {
  private readonly f: LedgerFetch;
  private readonly base: string;
  private readonly raid: RaidId;
  private readonly timeoutMs: number;
  private readonly now: () => number;
  private readonly characterId: () => string;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  /** 최소 간격 때문에 미룬 강제 sync(하나만 예약 — 겹친 요청은 같은 것을 받는다) */
  private trailing: Promise<void> | null = null;
  private readonly done = new Set<string>();
  private running: Promise<void> | null = null;
  /** 마지막으로 서버 장부를 받은 시각(0 = 아직) */
  syncedAt = 0;
  /** 연동 없음·스위치 꺼짐·로그인 없음이면 이 시각까지 다시 묻지 않는다 */
  private quietUntil = 0;
  /** 연속 실패 수·그 백오프가 끝나는 시각·마지막으로 네트워크를 친 시각 */
  private fails = 0;
  private backoffUntil = 0;
  private lastTryAt = -1e9;
  private ch = '';
  /** 마지막 결과(시험·QA 진단) */
  last: 'idle' | 'ok' | 'offline' | 'unlinked' | 'off' | 'error' = 'idle';

  constructor(private readonly g: RaidLedgerGame, o: RaidLedgerOptions = {}) {
    this.f = o.fetch ?? ((input, init) => fetch(input, init));
    this.base = o.base ?? '/api/raid';
    this.raid = o.raid ?? 'raid_wyvern';
    this.timeoutMs = o.timeoutMs ?? 10_000;
    this.now = o.now ?? (() => Date.now());
    this.characterId = o.characterId ?? (() => 'legacy');
    this.setTimer = o.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  }

  /** 올릴 영수증: 이번 주 로컬 영수증 + 하드 첫 클리어 중 서버 장부에 없고 이 세션에서 끝나지 않은 것 */
  pending(): string[] {
    const g = this.g, week = g.raidWeek(), srv = g.raidServer;
    const known = new Set(srv && srv.raid === this.raid && srv.week === week ? srv.ids : []);
    const out: string[] = [];
    g.save.forEachRaidReceipt((id) => {
      const at = parseRaidReceipt(id);
      if (!at || at.raid !== this.raid || this.done.has(id) || known.has('solo:' + id)) return;
      if (at.first ? id === raidFirstId(this.raid, 'hard') : at.week === week) out.push(id);
    });
    return out.sort();
  }

  /**
   * 로그인·구글 연동·계정 바뀜 이벤트: 조용한 시간(연동 없음·로그인 없음으로 쉬던 5분)과 백오프를 풀고 바로 강제 sync.
   * 방금 연동했는데 5분 동안 장부를 못 받아 이중 지급 창이 열리는 일을 막는다(정확성 P2-1)
   */
  wake(): Promise<void> {
    this.quietUntil = 0; this.backoffUntil = 0; this.fails = 0; this.lastTryAt = -1e9;
    return this.sync(true);
  }

  /**
   * 서버와 맞춘다(필요할 때만 네트워크). force면 조용한 시간·백오프도 무시(단 최소 간격은 지킨다).
   * 최소 간격에 걸린 강제 sync는 버리지 않고 lastTryAt + 간격에 한 번 뒤따라 실행한다(중복 예약 없음 — 겹친 요청은 같은 예약을 기다린다).
   * 입장 직전 sync(beforeConnect)가 방금 전 sync와 겹쳐도 최신 장부를 받고 들어가게 한다
   */
  sync(force = false): Promise<void> {
    if (this.running) return this.running;
    if (force) {
      const wait = this.lastTryAt + LEDGER_MIN_FORCE_GAP_MS - this.now();
      if (wait > 0) {
        this.trailing ??= new Promise<void>((resolve) => {
          this.setTimer(() => { this.trailing = null; this.go(true).then(resolve, resolve); }, wait);
        });
        return this.trailing;
      }
    }
    return this.go(force);
  }

  private go(force: boolean): Promise<void> {
    if (this.running) return this.running;
    this.running = this.run(force).finally(() => { this.running = null; });
    return this.running;
  }

  private async call(method: 'GET' | 'POST', path: string, body?: unknown): Promise<{ ok: boolean; status: number; body: unknown } | null> {
    const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = ctl ? setTimeout(() => ctl.abort(), this.timeoutMs) : 0;
    try {
      const res = await this.f(this.base + path, {
        method, credentials: 'same-origin', signal: ctl?.signal ?? undefined,
        headers: body === undefined ? undefined : { 'content-type': 'application/json' },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      let json: unknown = null;
      try { json = await res.json(); } catch { json = null; }
      return { ok: res.ok, status: res.status, body: json };
    } catch { return null; } finally { if (timer) clearTimeout(timer); }
  }

  private fail(t: number): void {
    this.fails++;
    this.backoffUntil = t + Math.min(LEDGER_BACKOFF_MS.max, LEDGER_BACKOFF_MS.first * 2 ** (this.fails - 1));
  }

  private async run(force: boolean): Promise<void> {
    const t = this.now();
    const ch = this.characterId() || 'legacy';
    // 캐릭터가 바뀌면 장부도 다른 장부다(캐릭터별) — 아는 것을 비운다
    if (ch !== this.ch) { this.ch = ch; this.done.clear(); this.g.raidServer = null; this.syncedAt = 0; this.quietUntil = 0; this.fails = 0; this.backoffUntil = 0; this.lastTryAt = -1e9; }
    if (!force && (t < this.quietUntil || t < this.backoffUntil)) return;
    this.lastTryAt = t;
    const receipts = this.pending();
    const r = receipts.length
      ? await this.call('POST', '/solo', { ch, raid: this.raid, receipts })
      : await this.call('GET', '/ledger?raid=' + encodeURIComponent(this.raid) + '&ch=' + encodeURIComponent(ch));
    if (ch !== this.characterId()) return; // 응답을 기다리는 사이 캐릭터가 바뀌었다
    if (!r) { this.last = 'offline'; this.fail(t); return; }
    if (!r.ok) {
      // 401(로그인 없음)·404(레이드 넷 꺼짐)·403(캐릭터) — 5분 뒤 다시(wake가 풀어 준다). 그 밖(빈도·서버 오류)은 지수 백오프 뒤 다시
      if (r.status === 401 || r.status === 403 || r.status === 404) { this.quietUntil = t + 300_000; this.g.raidServer = null; this.last = 'off'; } else { this.last = 'error'; this.fail(t); }
      return;
    }
    this.fails = 0; this.backoffUntil = 0;
    const body = r.body as { ledger?: LedgerBody; results?: { id: string; st: string }[] } | null;
    const ledger = ledgerFromBody(body?.ledger, this.raid);
    if (!ledger) { this.g.raidServer = null; this.quietUntil = t + 300_000; this.last = 'unlinked'; return; }
    for (const x of body?.results ?? []) if (x && typeof x.id === 'string' && x.st !== 'later' && x.st !== 'unlinked') this.done.add(x.id);
    this.g.raidServer = ledger;
    this.syncedAt = t;
    this.last = 'ok';
  }
}
